import {
  FormControlLabel,
  styled,
  TextField,
  TextFieldProps,
} from '@mui/material'
import {
  StyledDialog,
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogTitle,
  Title,
} from '.'
import {
  ChangeEvent,
  useCallback,
  useMemo,
  useState,
  KeyboardEvent,
  useEffect,
} from 'react'
import { colorPalette } from '../../../../style/colorPalette'
import { intl } from '../../../../../i18n'
import { useVersionReportVersionRepository } from '../../../../../services/adaptors/versionReportVersionRepositoryAdaptor'
import { Button } from '../../../../components/buttons'
import {
  ProgressOverviewPageConfig,
  ProgressReportByProcessPageConfig,
  ProgressReportByTeamPageConfig,
  VersionReportVersionEntity,
} from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import Loading from '../../../../components/process-state-notifications/Loading'
import { isAiAvailable } from '../../../../../lib/functions/ai'
import { Checkbox } from '../../../../components/inputs/Checkbox'

type NewVersionDialogProps = {
  open: boolean
  templateUuid: string | undefined
  onAfterSubmit: (
    uuid: string,
    name: string,
    isInitial: boolean,
    version: VersionReportVersionEntity
  ) => void
  onClose: () => void
}

export const NewVersionDialog = ({
  open,
  templateUuid,
  onAfterSubmit,
  onClose,
}: NewVersionDialogProps) => {
  const {
    fetch,
    createVersion,
    evaluateOverview,
    evaluateProgressByProcess,
    evaluateProgressByTeam,
  } = useVersionReportVersionRepository()
  const [value, setValue] = useState<string>('')
  const onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value)
  }, [])
  const [loading, setLoading] = useState<boolean>(false)
  const [isUseAi, setIsUseAi] = useState<boolean>(false)
  useEffect(() => {
    setIsUseAi(false)
  }, [open])

  const onSubmit = useCallback(async () => {
    if (!templateUuid) return
    setLoading(true)
    // 1. Create version. BE 側で前 Ver pages の自動 inherit を判定し、初回 (前 Ver
    //    なし or 前 Ver pages 空) なら isInitial: true を返す。
    const response = await createVersion(templateUuid, value)
    // 2. Fetch the created version
    const version = await fetch(response.uuid)
    if (!version) {
      setLoading(false)
      onClose()
      return
    }
    // 3. 初回の場合は AI 評価対象の pages が空。構成編集ダイアログ起動は親側
    //    (version-select-menu) に委ねる。
    if (response.isInitial) {
      onAfterSubmit(response.uuid, value, true, version)
      setLoading(false)
      onClose()
      return
    }
    // 4. 2 回目以降: 前 Ver pages を inherit 済み、AI 評価を実行
    const isUserUseAi = isAiAvailable() && isUseAi
    if (isUserUseAi) {
      const evaluateRequests = version.pages.map(page => {
        switch (page.pageType) {
          case 'PROGRESS_OVERVIEW':
            return evaluateOverview({
              snapshotUuid: version.snapshotUuid,
              leafUuids: (page.config as ProgressOverviewPageConfig).tableRows,
            })
          case 'PROGRESS_REPORT_BY_PROCESS':
            return evaluateProgressByProcess({
              snapshotUuid: version.snapshotUuid,
              projectPlanUuids: (
                page.config as ProgressReportByProcessPageConfig
              ).tableRows,
            })
          case 'PROGRESS_REPORT_BY_TEAM':
            return evaluateProgressByTeam({
              snapshotUuid: version.snapshotUuid,
              rootUuid: (page.config as ProgressReportByTeamPageConfig)
                .rootUuid,
              teamUuids: (page.config as ProgressReportByTeamPageConfig)
                .tableRows,
            })
        }
      })
      // TODO(FE#4): AI 評価結果を rows に反映する処理を新 API で実装
      await Promise.all(evaluateRequests)
    }

    onAfterSubmit(response.uuid, value, false, version)
    setLoading(false)
    onClose()
  }, [
    createVersion,
    evaluateOverview,
    evaluateProgressByProcess,
    evaluateProgressByTeam,
    fetch,
    onAfterSubmit,
    onClose,
    templateUuid,
    value,
    isUseAi,
  ])

  const onEnter = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        onSubmit()
      }
    },
    [onSubmit]
  )

  const disabled = useMemo(() => !value, [value])
  return (
    <>
      <StyledDialog open={open} onClose={onClose}>
        <StyledDialogTitle>
          <Title>報告書の新規作成</Title>
        </StyledDialogTitle>
        <StyledDialogContent>
          <Input
            label={'報告書の名称'}
            onChange={onChange}
            onKeyDown={onEnter}
          />
          <CheckboxContainer>
            <CheckboxFormControlLabel
              control={
                <Checkbox
                  checked={isUseAi}
                  onChange={() => setIsUseAi(prev => !prev)}
                />
              }
              label="AIを使用してステータス及びコメントを生成する"
            />
          </CheckboxContainer>
        </StyledDialogContent>
        <StyledDialogActions>
          <Button onClick={onSubmit} disabled={disabled} colorPattern="skyBlue">
            報告書を作成する
          </Button>
          <Button onClick={onClose} colorPattern="monotone" variant="outlined">
            {intl.formatMessage({ id: 'dialog.cancel' })}
          </Button>
        </StyledDialogActions>
      </StyledDialog>
      <Loading isLoading={loading} />
    </>
  )
}

const Input = styled((props: TextFieldProps) => (
  <TextField variant="outlined" {...props} />
))({
  width: '360px',
  '& .MuiOutlinedInput-input': {
    height: '14px',
    padding: '20px 16px',
    color: colorPalette.monotone[9],
    caretColor: colorPalette.skyBlue[7],
    '&:focus': {
      backgroundColor: colorPalette.blue[0],
    },
    fontSize: '16px !important',
  },
  '& .MuiOutlinedInput-root': {
    height: '54px',
    '& fieldset': {
      borderColor: colorPalette.monotone[2],
    },
    '&:hover fieldset': {
      borderColor: colorPalette.skyBlue[7],
    },
    '&.Mui-focused fieldset': {
      borderColor: colorPalette.skyBlue[7],
      borderWidth: '1px',
    },
  },
})

const CheckboxContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  marginTop: '12px',
})

const CheckboxFormControlLabel = styled(FormControlLabel)({
  margin: 0,
  alignItems: 'center',
  '& .MuiFormControlLabel-label': {
    color: colorPalette.monotone[9],
  },
})
